import { Heart } from "lucide-react";
import { ReactionButtons } from "../ReactionButtons";

export const InclusionInsightCard = ({ stats }) => {
  const lgbtq = stats?.lgbtq_stats;
  const focus = stats?.lgbtq_focus_stats;
  const hasLgbtq = lgbtq?.total > 0;
  const hasFocus = focus?.total > 0;

  if (!hasLgbtq && !hasFocus) return null;

  return (
    <div
      className="bg-gradient-to-r from-violet-500/10 via-pink-500/10 to-amber-500/10 border border-violet-500/20 rounded-xl p-5"
      data-testid="inclusion-insight-card"
    >
      <div className="flex items-start gap-4">
        <div className="p-2 rounded-full bg-violet-500/20 flex-shrink-0">
          <Heart className="w-4 h-4 text-violet-400" />
        </div>
        <div className="flex-1 space-y-4">
          <h3 className="text-base font-semibold text-white/90">
            Inclusion & Representation: What is Humanity Solving For?
          </h3>

          {hasLgbtq && (
            <div>
              <p className="text-xs font-semibold text-violet-400 uppercase tracking-wider mb-1.5">
                By Founder Identity
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="text-violet-400 font-semibold">{lgbtq.total}</span> publicly declared LGBTQIA+ founded AI apps tracked.{" "}
                <span className="text-pink-400 font-semibold">{lgbtq.care_percent}%</span> are care-oriented,
                focusing on healthcare access, ethical AI, and community platforms.
                This data reflects only self-declared information; the true number is likely far higher.
              </p>
              <div className="flex gap-5 mt-2">
                <MetricStat value={lgbtq.total} label="LGBTQIA+ Founded" color="text-violet-400" />
                <MetricStat value={`${lgbtq.care_percent}%`} label="Care-Oriented" color="text-pink-400" />
                <MetricStat
                  value={stats?.founder_distribution?.female_only || 0}
                  label="Female-Founded"
                  color="text-amber-400"
                />
              </div>
            </div>
          )}

          {hasFocus && (
            <div className="pt-3 border-t border-white/[0.06]">
              <p className="text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-1.5">
                By App Focus
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="text-cyan-400 font-semibold">{focus.total}</span> AI apps directly serve LGBTQIA+ communities, through healthcare, dating, inclusion tools, and advocacy.{" "}
                <span className="text-pink-400 font-semibold">{focus.care_percent}%</span> are care-oriented.
                {focus.top_countries?.length > 0 && (
                  <> Primarily from {focus.top_countries.map(c => c.country).join(", ")}.</>
                )}
              </p>
              <div className="flex gap-5 mt-2">
                <MetricStat value={focus.total} label="LGBTQIA+ Focused Apps" color="text-cyan-400" />
                <MetricStat value={`${focus.care_percent}%`} label="Care-Oriented" color="text-pink-400" />
                <MetricStat
                  value={focus.top_countries?.length || 0}
                  label="Countries"
                  color="text-emerald-400"
                />
              </div>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground/50 italic pt-1">
            Note: Founder identity data is based on publicly available information. App focus is identified through content analysis. Both likely undercount LGBTQIA+ representation.
          </p>
          <div className="pt-2">
            <ReactionButtons insightId="inclusion-insight" />
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricStat = ({ value, label, color }) => (
  <div className="text-center">
    <div className={`text-base font-bold ${color}`}>{value}</div>
    <div className="text-[10px] text-muted-foreground">{label}</div>
  </div>
);

export default InclusionInsightCard;
